import React, { useEffect, useState } from "react";
import SummaryApi from "../common";
import * as XLSX from "xlsx"; // Importing the xlsx library
import noData from "../Assets/noData.png";
const ContactReport = () => {
  const [contacts, setContacts] = useState([]); // All contacts
  const [filteredContacts, setFilteredContacts] = useState([]); // Filtered contacts based on date
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(""); // Date filter state
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [contactsPerPage] = useState(10); // Number of records to display per page
  // Fetch data from the API using fetch
  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const response = await fetch(SummaryApi.adminContact.url, {
          method: SummaryApi.adminContact.method, // GET method
        });
        const data = await response.json();
        if (data.status) {
          setContacts(data.data);
          setFilteredContacts(data.data); // Initially, no filter is applied
        } else {
          setError("Failed to fetch contact data.");
        }
      } catch (error) {
        setError("Error fetching data.");
      } finally {
        setLoading(false);
      }
    };
    fetchContacts();
  }, []); // Empty dependency array means this effect will run once after the component mounts
  // Excel Export Handler
  const exportToExcel = () => {
    const modifiedContacts = filteredContacts.map((contact, index) => ({
      "S.no": index + 1, // Add the serial number
      "Student Name": contact.student_name,
      "Parent Name": contact.parent_name,
      "Mobile Number": contact.mobile_number,
      "Address": contact.address,
      "Interested Course": contact.course,
      "Is Active": contact.is_active === "1" ? "Active" : "Inactive",
      "Created Date": new Date(contact.created_date)
        .toISOString()
        .slice(0, 19)
        .replace("T", " "),
    }));
    const ws = XLSX.utils.json_to_sheet(modifiedContacts); // Convert the modified contacts to a worksheet
    const wb = XLSX.utils.book_new(); // Create a new workbook
    XLSX.utils.book_append_sheet(wb, ws, "Contacts"); // Append the worksheet to the workbook
    // Generate and download the Excel file
    XLSX.writeFile(wb, "contacts_report.xlsx");
  };
  // Filter function to filter contacts based on the selected date
  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
    if (e.target.value === "") {
      setFilteredContacts(contacts); // No filter applied, show all contacts
    } else {
      const filtered = contacts.filter((contact) =>
        contact.created_date.startsWith(e.target.value)
      );
      setFilteredContacts(filtered); // Apply the filter based on the selected date
    }
  };
  // Pagination logic
  const indexOfLastContact = currentPage * contactsPerPage;
  const indexOfFirstContact = indexOfLastContact - contactsPerPage;
  const currentContacts = filteredContacts.slice(indexOfFirstContact, indexOfLastContact);
  const totalPages = Math.ceil(filteredContacts.length / contactsPerPage);
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  if (loading) {
    return <div className="text-center text-xl">Loading...</div>;
  }
  if (error) {
    return <div className="text-center text-red-600">{error}</div>;
  }
  return (
    <div className="mx-auto p-4">
      <h1 className="text-2xl font-semibold text-gray-800 mb-6 text-center">
        Contact Report
      </h1>
      <div className="flex justify-between">
        {/* Date filter */}
        <div className="mb-4 text-center">
          <label
            htmlFor="dateFilter"
            className="mr-2 text-sm font-medium text-gray-700"
          >
            Filter by Date:
          </label>
          <input
            type="date"
            id="dateFilter"
            value={selectedDate}
            onChange={handleDateChange}
            className="py-1 px-3 border border-gray-300 rounded-md"
          />
        </div>
        {/* Button to export to Excel */}
        <div className="mb-4 text-center">
          <button
            onClick={exportToExcel}
            className="bg-blue-500 text-white py-1 px-4 rounded hover:bg-blue-600"
          >
            Download Excel
          </button>
        </div>
      </div>
      <div className="">
        {/* Check if there are no filtered contacts */}
        {currentContacts.length === 0 ? (
          <div className="text-center">
            <img
              src={noData}
              alt="No Data Found"
              className="mx-auto my-4"
              width={200}
            />
            <p className="text-xl text-gray-600">No data found.</p>
          </div>
        ) : (
          <table className="min-w-full table-auto border-separate border border-gray-200 rounded-lg shadow-md">
            <thead>
              <tr className="bg-gray-100 text-left">
                <th className="px-4 py-2 text-sm font-semibold text-gray-600">S.no</th>
                <th className="px-4 py-2 text-sm font-semibold text-gray-600">Student Name</th>
                <th className="px-4 py-2 text-sm font-semibold text-gray-600">Mobile Number</th>
                <th className="px-4 py-2 text-sm font-semibold text-gray-600">Parent Name</th>
                <th className="px-4 py-2 text-sm font-semibold text-gray-600">Email</th>
                <th className="px-4 py-2 text-sm font-semibold text-gray-600">Address</th>
                <th className="px-4 py-2 text-sm font-semibold text-gray-600">Course</th>
                <th className="px-4 py-2 text-sm font-semibold text-gray-600">Is Active</th>
                <th className="px-4 py-2 text-sm font-semibold text-gray-600">Created Date</th>
              </tr>
            </thead>
            <tbody>
              {currentContacts.map((contact, index) => (
                <tr
                  key={contact.contact_form_id}
                  className="border-t border-gray-200 hover:bg-gray-50"
                >
                  <td className="px-4 py-2 text-sm text-gray-700">{index + 1}</td>
                  <td className="px-4 py-2 text-sm text-gray-700">{contact.student_name}</td>
                  <td className="px-4 py-2 text-sm text-gray-700">{contact.mobile_number}</td>
                  <td className="px-4 py-2 text-sm text-gray-700">{contact.parent_name}</td>
                  <td className="px-4 py-2 text-sm text-gray-700">{contact.email}</td>
                  <td className="px-4 py-2 text-sm text-gray-700">{contact.address}</td>
                  <td className="px-4 py-2 text-sm text-gray-700">{contact.course}</td>
                  <td className="px-4 py-2 text-sm text-gray-700">
                    {contact.is_active === "1" ? (
                      <span className="text-green-500 font-semibold">Active</span>
                    ) : (
                      <span className="text-red-500 font-semibold">Inactive</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-700">
                    {new Date(contact.created_date).toISOString().slice(0, 19).replace("T", " ")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {/* Pagination Controls */}
      <div className="flex justify-center mt-6">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-4 py-1 bg-blue-500 text-white rounded-md disabled:bg-gray-400"
        >
          Prev
        </button>
        <span className="mx-4">
          Page {currentPage} of {totalPages}
        </span>
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-4 py-1 bg-blue-500 text-white rounded-md disabled:bg-gray-400"
        >
          Next
        </button>
      </div>
    </div>
  );
};
export default ContactReport;